import { motion } from "framer-motion";
import { useState } from "react";
import { parseDateString, endOrNow } from "../../lib/dates";
import { ITEM_HEIGHT, LANE_HEIGHT, LANE_GAP } from "./constants";
import type { TimelineScales } from "../../hooks/useTimelineScales";

interface GenericBarProps {
  id: string;
  startDate: string;
  endDate: string | null;
  subLane: number;
  label: string;
  scales: TimelineScales;
  laneOffsetY: number;
  color: string;
  onHover: (id: string | null, anchor: { x: number; y: number } | null) => void;
}

export function GenericBar({
  id,
  startDate,
  endDate,
  subLane,
  label,
  scales,
  laneOffsetY,
  color,
  onHover,
}: GenericBarProps) {
  const [hovered, setHovered] = useState(false);
  const start = parseDateString(startDate)!;
  const end = endOrNow(parseDateString(endDate));

  const pillH = ITEM_HEIGHT - 2;
  const x = scales.xScale(start);
  const w = Math.max(scales.xScale(end) - x, pillH);
  const y =
    laneOffsetY + subLane * (LANE_HEIGHT + LANE_GAP) + (LANE_HEIGHT - ITEM_HEIGHT) / 2;
  const rx = pillH / 2;

  return (
    <motion.g
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, y }}
      transition={{ duration: 0.45, ease: [0.22, 0.36, 0.27, 1] }}
      style={{ cursor: "pointer" }}
      onMouseEnter={(e) => {
        setHovered(true);
        onHover(id, { x: e.clientX, y: e.clientY });
      }}
      onMouseLeave={() => {
        setHovered(false);
        onHover(null, null);
      }}
    >
      <rect
        x={x}
        y={1}
        width={w}
        height={pillH}
        rx={rx}
        ry={rx}
        fill={color}
        fillOpacity={hovered ? 0.32 : 0.16}
        stroke={color}
        strokeOpacity={hovered ? 1 : 0.85}
        strokeWidth={1.2}
        style={{ transition: "fill-opacity 200ms ease, stroke-opacity 200ms ease" }}
      />
      {w > 60 && (
        <text
          x={x + rx + 4}
          y={pillH / 2 + 4}
          fill="var(--color-cream)"
          fontSize={11}
          fontFamily="var(--font-body)"
          fontWeight={500}
          letterSpacing="-0.005em"
          className="select-none pointer-events-none"
        >
          {truncate(label, Math.floor((w - rx * 2) / 6))}
        </text>
      )}
    </motion.g>
  );
}

function truncate(s: string, max: number): string {
  if (max <= 0) return "";
  if (s.length <= max) return s;
  return s.slice(0, Math.max(0, max - 1)) + "…";
}
